import React, { useEffect, useState, useCallback } from 'react';
import { apiGet } from '../../lib/api-client';

interface PlayerProfile {
  playerName: string;
  totalHands: number;
  vpipHands: number;
  pfrHands: number;
  vpip: number;
  pfr: number;
  vpipPfrGap: number;
  aggressionFactor: number | null;
  wtsdPct: number;
  playerType: string;
}

interface TypeSummary {
  playerType: string;
  count: number;
  avgVpip: number;
  avgPfr: number;
  avgAf: number | null;
  avgHands: number;
}

interface SummaryResponse {
  totalPlayers: number;
  minHandsFilter: number;
  types: TypeSummary[];
}

const TYPE_COLORS: Record<string, string> = {
  TAG: '#22c55e',
  LAG: '#f59e0b',
  Nit: '#6366f1',
  'Calling Station': '#ef4444',
  Maniac: '#ec4899',
  Unknown: '#6b7280',
};

const TYPE_DESCRIPTIONS: Record<string, string> = {
  TAG: 'Tight-Aggressive: Selective hand picking, aggressive betting. The standard winning style.',
  LAG: 'Loose-Aggressive: Wide range, high aggression. Skilled players who pressure opponents.',
  Nit: 'Tight-Passive / Rock: Ultra-conservative, only plays premium hands.',
  'Calling Station': 'Loose-Passive: Plays many hands but rarely raises. Calls too much.',
  Maniac: 'Hyper-Aggressive: Plays nearly every hand with maximum aggression.',
  Unknown: 'Not enough hands to classify reliably.',
};

export function PlayersScreen() {
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [players, setPlayers] = useState<PlayerProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [minHands, setMinHands] = useState(30);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('total_hands');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const loadSummary = useCallback(async () => {
    try {
      const data = await apiGet<SummaryResponse>('/api/players/summary', {
        minHands: String(minHands),
      });
      setSummary(data);
    } catch {
      // Summary might fail if table doesn't exist yet
    }
  }, [minHands]);

  const loadPlayers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        sort,
        order,
        limit: String(pageSize),
        offset: String(page * pageSize),
        minHands: String(minHands),
      };
      if (typeFilter) params.type = typeFilter;
      if (search) params.search = search;

      const data = await apiGet<{ total: number; profiles: PlayerProfile[] }>('/api/players', params);
      setPlayers(data.profiles);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load players');
    } finally {
      setLoading(false);
    }
  }, [sort, order, page, typeFilter, minHands, search]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadPlayers(); }, [loadPlayers]);

  function handleSort(col: string) {
    if (sort === col) {
      setOrder(o => o === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(col);
      setOrder('desc');
    }
    setPage(0);
  }

  function sortIndicator(col: string) {
    if (sort !== col) return '';
    return order === 'desc' ? ' \u25BC' : ' \u25B2';
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="players-screen">
      <h2>Player Profiles</h2>
      <p className="players-subtitle">
        VPIP/PFR analysis from hand histories. Run <code>npm run db:analyze-players</code> to compute.
      </p>

      {/* Type Distribution Summary */}
      {summary && summary.types.length > 0 && (
        <div className="players-summary">
          <h3>Player Type Distribution ({summary.totalPlayers} players, min {summary.minHandsFilter} hands)</h3>
          <div className="type-cards">
            {summary.types.map(t => (
              <button
                key={t.playerType}
                className={`type-card ${typeFilter === t.playerType ? 'active' : ''}`}
                style={{ borderColor: TYPE_COLORS[t.playerType] ?? '#6b7280' }}
                onClick={() => {
                  setTypeFilter(f => f === t.playerType ? '' : t.playerType);
                  setPage(0);
                }}
              >
                <div className="type-card-header">
                  <span
                    className="type-badge"
                    style={{ backgroundColor: TYPE_COLORS[t.playerType] ?? '#6b7280' }}
                  >
                    {t.playerType}
                  </span>
                  <span className="type-count">{t.count}</span>
                </div>
                <div className="type-card-stats">
                  <span>VPIP: {t.avgVpip.toFixed(1)}%</span>
                  <span>PFR: {t.avgPfr.toFixed(1)}%</span>
                  {t.avgAf != null && <span>AF: {t.avgAf.toFixed(1)}</span>}
                </div>
                <div className="type-card-desc">
                  {TYPE_DESCRIPTIONS[t.playerType]?.split('.')[0]}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="players-filters">
        <input
          type="text"
          placeholder="Search player name..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          className="players-search"
        />
        <label className="players-min-hands">
          Min hands:
          <input
            type="number"
            value={minHands}
            onChange={e => { setMinHands(parseInt(e.target.value) || 0); setPage(0); }}
            min={0}
            step={10}
          />
        </label>
        {typeFilter && (
          <button className="players-clear-filter" onClick={() => { setTypeFilter(''); setPage(0); }}>
            Clear filter: {typeFilter} &times;
          </button>
        )}
      </div>

      {error && <div className="players-error">{error}</div>}

      {/* Table */}
      <div className="players-table-wrap">
        <table className="players-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('player_name')}>Player{sortIndicator('player_name')}</th>
              <th onClick={() => handleSort('total_hands')}>Hands{sortIndicator('total_hands')}</th>
              <th onClick={() => handleSort('vpip')}>VPIP{sortIndicator('vpip')}</th>
              <th onClick={() => handleSort('pfr')}>PFR{sortIndicator('pfr')}</th>
              <th onClick={() => handleSort('vpip_pfr_gap')}>Gap{sortIndicator('vpip_pfr_gap')}</th>
              <th onClick={() => handleSort('aggression_factor')}>AF{sortIndicator('aggression_factor')}</th>
              <th onClick={() => handleSort('wtsd_pct')}>WTSD{sortIndicator('wtsd_pct')}</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="players-loading">Loading...</td></tr>
            ) : players.length === 0 ? (
              <tr><td colSpan={8} className="players-empty">
                No players found. Run <code>npm run db:analyze-players</code> after seeding.
              </td></tr>
            ) : players.map(p => (
              <tr key={p.playerName}>
                <td className="player-name-cell">{p.playerName}</td>
                <td className="num">{p.totalHands.toLocaleString()}</td>
                <td className="num">{p.vpip.toFixed(1)}%</td>
                <td className="num">{p.pfr.toFixed(1)}%</td>
                <td className="num">{p.vpipPfrGap.toFixed(1)}</td>
                <td className="num">{p.aggressionFactor != null ? p.aggressionFactor.toFixed(1) : 'N/A'}</td>
                <td className="num">{p.wtsdPct.toFixed(1)}%</td>
                <td>
                  <span
                    className="type-badge-small"
                    style={{ backgroundColor: TYPE_COLORS[p.playerType] ?? '#6b7280' }}
                  >
                    {p.playerType}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="players-pagination">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span>Page {page + 1} of {totalPages} ({total} players)</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
